import { httpsCallable } from "firebase/functions";
import { signInWithCustomToken, signOut } from "firebase/auth";
import { auth, functions } from "../firebase/config";
import { isTauriMode } from "./environmentDetector";
import tauriAPI from "./tauriAPI";

export const CONNECTION_EVENT = "workstation-connection";
const CONNECTION_KEY = "workstationConnection";

let claimInFlight = null;
let prepareInFlight = null;

export function waitForWorkstationSecrets() {
  return prepareInFlight || Promise.resolve();
}

export function prepareWorkstationSecrets(storeIds) {
  if (!isTauriMode()) return Promise.resolve();
  const ids = [...new Set((storeIds || []).map((id) => String(id || "")).filter(Boolean))];
  if (!ids.length) return Promise.resolve();
  if (prepareInFlight) return prepareInFlight;
  prepareInFlight = (async () => {
    const existing = await currentWorkstation();
    if (existing.workstation) return;
    let fingerprint = "";
    try {
      const hardware = await tauriAPI.system.getStationFingerprint();
      fingerprint = String(hardware?.fingerprint || "").trim().toLowerCase();
    } catch {
      return;
    }
    if (!/^[0-9a-f]{64}$/.test(fingerprint)) return;
    for (const storeId of ids) {
      const saved = await readSecretForStore(storeId);
      if (saved?.secret) continue;
      const signedIn = await currentWorkstation();
      if (signedIn.workstation) return;
      try {
        const call = httpsCallable(functions, "claimStationConnection");
        const result = await call({ storeId, fingerprint });
        const deviceSecret = result.data?.deviceSecret;
        if (!deviceSecret) continue;
        await tauriAPI.system.saveStationConnectionSecret(storeId, fingerprint, deviceSecret);
      } catch {
        // This store has no claim for this PC yet. It stays out of the switch until it does.
      }
    }
  })().finally(() => {
    prepareInFlight = null;
  });
  return prepareInFlight;
}

export function readWorkstationConnection() {
  try {
    return localStorage.getItem(CONNECTION_KEY) === "on";
  } catch {
    return false;
  }
}

export function writeWorkstationConnection(on) {
  try {
    localStorage.setItem(CONNECTION_KEY, on ? "on" : "off");
  } catch {
    // The indication is optional when storage is blocked.
  }
  window.dispatchEvent(new Event(CONNECTION_EVENT));
}

function selectedStoreId() {
  try {
    return localStorage.getItem("selectedStore") || "";
  } catch {
    return "";
  }
}

export async function currentWorkstation() {
  const user = auth.currentUser;
  if (!user || user.isAnonymous) return { workstation: false, storeId: "" };
  try {
    const result = await user.getIdTokenResult();
    return {
      workstation: result.claims.workstation === true,
      storeId: String(result.claims.storeId || ""),
    };
  } catch {
    return { workstation: false, storeId: "" };
  }
}

function failedClosed(error) {
  const code = String(error?.code || "");
  return code.includes("failed-precondition")
    || code.includes("permission-denied")
    || code.includes("not-found")
    || code.includes("unauthenticated");
}

async function readSecrets() {
  if (!isTauriMode()) return {};
  try {
    const file = await tauriAPI.system.readStationConnectionSecret();
    const stores = file?.stores;
    if (!stores || typeof stores !== "object") return {};
    return stores;
  } catch {
    return {};
  }
}

async function readSecretForStore(storeId) {
  if (!storeId) return null;
  const entry = (await readSecrets())[storeId];
  if (!entry?.secret || !entry.fingerprint) return null;
  return { storeId, fingerprint: entry.fingerprint, secret: entry.secret };
}

async function removeSecret(storeId) {
  if (!isTauriMode() || !storeId) return;
  try {
    await tauriAPI.system.removeStationConnectionSecret(storeId);
  } catch {
    // The entry is already gone.
  }
}

let presenceTimer = null;

function stopPresence() {
  if (presenceTimer) {
    clearInterval(presenceTimer);
    presenceTimer = null;
  }
}

function connectionRevoked(error) {
  return String(error?.code || "").includes("failed-precondition");
}

export async function resumeStationConnection(storeId) {
  if (!isTauriMode()) return "none";
  await auth.authStateReady();
  const selected = String(storeId || selectedStoreId() || "");
  if (!selected) return "none";
  const existing = await currentWorkstation();
  if (existing.workstation && existing.storeId === selected) {
    writeWorkstationConnection(true);
    return "on";
  }
  const saved = await readSecretForStore(selected);
  if (!saved) {
    if (existing.workstation) {
      writeWorkstationConnection(false);
      await signOut(auth);
    }
    return "none";
  }
  if (typeof navigator !== "undefined" && navigator.onLine === false) return "offline";
  try {
    const call = httpsCallable(functions, "resumeStationConnection");
    const result = await call({
      storeId: saved.storeId,
      fingerprint: saved.fingerprint,
      deviceSecret: saved.secret,
    });
    const token = result.data?.customToken;
    if (!token) throw new Error("Workstation connection is off.");
    await signInWithCustomToken(auth, token);
    try { localStorage.setItem("selectedStore", selected); } catch { /* The selected store is already this store. */ }
    writeWorkstationConnection(true);
    watchStationPresence();
    return "on";
  } catch (error) {
    if (failedClosed(error)) {
      await removeSecret(selected);
      writeWorkstationConnection(false);
      if (existing.workstation && existing.storeId === selected) {
        await signOut(auth);
      }
      return "off";
    }
    return "offline";
  }
}

export async function claimStationOnThisPc({ storeId, fingerprint }) {
  if (!isTauriMode() || !storeId || !fingerprint) return;
  await waitForWorkstationSecrets();
  const existing = await currentWorkstation();
  if (existing.workstation && existing.storeId === storeId) {
    writeWorkstationConnection(true);
    return;
  }
  const saved = await readSecretForStore(storeId);
  if (saved?.secret) {
    await resumeStationConnection(storeId);
    return;
  }
  if (existing.workstation) return;
  if (claimInFlight) return claimInFlight;
  claimInFlight = (async () => {
    const call = httpsCallable(functions, "claimStationConnection");
    const result = await call({ storeId, fingerprint });
    const token = result.data?.customToken;
    const deviceSecret = result.data?.deviceSecret;
    if (!token || !deviceSecret) return;
    await tauriAPI.system.saveStationConnectionSecret(storeId, fingerprint, deviceSecret);
    try { localStorage.setItem("selectedStore", storeId); } catch { /* ignore */ }
    await signInWithCustomToken(auth, token);
    writeWorkstationConnection(true);
    watchStationPresence();
  })().finally(() => {
    claimInFlight = null;
  });
  return claimInFlight;
}

export async function connectStationForStore({ storeId, fingerprint }) {
  await waitForWorkstationSecrets();
  const saved = await readSecretForStore(storeId);
  if (saved?.secret) {
    await resumeStationConnection(storeId);
    return;
  }
  const existing = await currentWorkstation();
  if (existing.workstation && existing.storeId !== storeId) {
    writeWorkstationConnection(false);
    await signOut(auth);
    return;
  }
  await claimStationOnThisPc({ storeId, fingerprint });
}

export async function listConnectedStores() {
  const existing = await currentWorkstation();
  if (!existing.workstation || !isTauriMode()) return [];
  const saved = await readSecretForStore(existing.storeId);
  if (!saved?.fingerprint) return [];
  const call = httpsCallable(functions, "listWorkstationStores");
  const result = await call({ fingerprint: saved.fingerprint });
  return Array.isArray(result.data?.stores) ? result.data.stores : [];
}

export function watchStationPresence() {
  if (presenceTimer) return;
  const beat = async () => {
    const existing = await currentWorkstation();
    if (!existing.workstation) return;
    try {
      const call = httpsCallable(functions, "reportStationConnection");
      await call({});
    } catch (error) {
      if (connectionRevoked(error)) await dropStationConnection(existing.storeId);
    }
  };
  beat();
  presenceTimer = setInterval(beat, 60000);
}

export async function dropStationConnection(storeId) {
  stopPresence();
  const existing = await currentWorkstation();
  const target = String(storeId || existing.storeId || selectedStoreId() || "");
  if (target) await removeSecret(target);
  const stillHere = existing.workstation && existing.storeId && existing.storeId !== target;
  if (!stillHere) writeWorkstationConnection(false);
  if (existing.workstation && existing.storeId === target) {
    await signOut(auth);
  }
}
